import { CategoryContent } from "@/components/ui/filters/CategoryContent";
import { Col } from "@/components/ui/Col";
import { Row } from "@/components/ui/Row";
import { Close, SearchIcon } from "@/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import "./FilterSearchSection.css";

type FilterItem = {
  id: string;
  label: string;
  checked: boolean;
};

type FilterSearchSectionProps = {
  items: FilterItem[];
  selectedItems: string[];
  onChange: (items: string[]) => void;
  searchPlaceholder: string;
  canMultiple?: boolean;
};

export const FilterSearchSection = ({
  items,
  selectedItems,
  onChange,
  searchPlaceholder,
  canMultiple = true,
}: FilterSearchSectionProps) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    const lowered = query.trim().toLowerCase();
    if (!lowered) return items;
    return items.filter((item) => item.label.toLowerCase().includes(lowered));
  }, [items, query]);

  const selectedChips = useMemo(() => {
    if (!selectedItems.length) return [];
    const labels = new Map(items.map((item) => [item.id, item.label]));
    return selectedItems.map((id) => ({
      id,
      label: labels.get(id) ?? id,
    }));
  }, [items, selectedItems]);

  const handleRemoveChip = (id: string) => {
    onChange(selectedItems.filter((itemId) => itemId !== id));
  };

  return (
    <Col className="ui-filter-search-section">
      <Col className="ui-filter-search-body" ref={dropdownRef}>
        <div className="ui-filter-search-dropdown-anchor">
          <Row className="ui-filter-search-input-row">
            <SearchIcon fillColor="#5C6F82" />
            <input
              className="ui-filter-search-input"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={searchPlaceholder}
            />
          </Row>

          <div
            className={`ui-filter-search-list${isOpen ? " is-open" : ""}`}
            aria-hidden={!isOpen}
          >
            <CategoryContent
              canMultiple={canMultiple}
              items={filteredItems}
              selectedItems={selectedItems}
              onChange={onChange}
            />
          </div>
        </div>

        {selectedChips.length > 0 && (
          <div className="ui-filter-chips">
            {selectedChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className="ui-filter-chip"
                onClick={() => handleRemoveChip(chip.id)}
              >
                <span>{chip.label}</span>
                <Close
                  className="ui-filter-chip-close"
                  width={24}
                  height={24}
                  decorative
                />
              </button>
            ))}
          </div>
        )}
      </Col>
    </Col>
  );
};

